/**
 * useImageUpload Hook - Story 6.3: Create Add Product Form
 * Image management hook for product image uploads
 */

import { useState, useCallback, useRef } from 'react';
import { Image } from '../types/productForm.types';
import { FORM_CONFIG } from '../constants/productFormConstants';
import { toast } from '@/lib/toast';
import { uploadProductImages } from '../services/productFormService';

// ============================================================================
// Types
// ============================================================================

/**
 * UseImageUpload Hook Return Type
 * Return value of the useImageUpload custom hook
 */
export interface UseImageUploadReturn {
  /** Array of images (local files with previews) */
  images: Image[];
  /** Add new images to the array */
  addImages: (files: File[]) => void;
  /** Remove an image by index */
  removeImage: (index: number) => void;
  /** Reorder images (drag and drop) */
  reorderImages: (fromIndex: number, toIndex: number) => void;
  /** Flag indicating upload in progress */
  isUploading: boolean;
  /** Array of successfully uploaded image IDs */
  uploadedImageIds: string[];
  /** Clear all images */
  clearAllImages: () => void;
  /** Register uploaded image ID (called after successful API upload) */
  registerUploadedImage: (index: number, imageId: string) => void;
  /** Get remaining slots available for images */
  getRemainingSlots: () => number;
  /** Trigger file input click */
  triggerFileInput: () => void;
  /** Reference to the file input element */
  fileInputRef: React.RefObject<HTMLInputElement>;
  /** Set images directly (for restoring state) */
  setImages: (images: Image[]) => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * useImageUpload Hook
 * Manages image selection, validation, preview generation, and upload state
 *
 * @param maxImages - Maximum number of images allowed (default: 5)
 * @param maxSizeBytes - Maximum file size in bytes (default: 5MB)
 * @param productId - Optional product ID for immediate upload in edit mode
 * @returns UseImageUploadReturn interface with image state and methods
 *
 * @example
 * const {
 *   images,
 *   addImages,
 *   removeImage,
 *   isUploading,
 *   triggerFileInput,
 *   fileInputRef,
 * } = useImageUpload();
 *
 * // Handle file input change
 * <input
 *   type="file"
 *   ref={fileInputRef}
 *   onChange={(e) => addImages(Array.from(e.target.files || []))}
 *   accept=".jpg,.jpeg,.png,.webp"
 *   multiple
 * />
 *
 * // Trigger file selection
 * <Button onClick={triggerFileInput}>Add Images</Button>
 */
export function useImageUpload(
  maxImages: number = FORM_CONFIG.MAX_IMAGES,
  maxSizeBytes: number = FORM_CONFIG.MAX_FILE_SIZE_BYTES,
  productId?: string
): UseImageUploadReturn {
  // ============================================================================
  // State
  // ============================================================================

  const [images, setImages] = useState<Image[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedImageIds, setUploadedImageIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ============================================================================
  // Validation Methods
  // ============================================================================

  /**
   * Validate a single file
   * @param file - File to validate
   * @returns Validation result with valid flag and optional error message
   */
  const validateFile = useCallback(
    (file: File): { valid: boolean; error?: string } => {
      // Check file type
      const allowedTypes = FORM_CONFIG.ALLOWED_IMAGE_TYPES as readonly string[];
      if (!allowedTypes.includes(file.type)) {
        return {
          valid: false,
          error: 'Only JPEG, PNG, and WebP images are allowed',
        };
      }

      // Check file size
      if (file.size > maxSizeBytes) {
        const maxSizeMB = maxSizeBytes / (1024 * 1024);
        return {
          valid: false,
          error: `File size must be less than ${maxSizeMB}MB`,
        };
      }

      return { valid: true };
    },
    [maxSizeBytes]
  );

  // ============================================================================
  // Image Management Methods
  // ============================================================================

  /**
   * Add images to the array with validation and preview generation
   * In edit mode with productId, uploads images immediately to server
   * In edit mode, automatically removes existing images and replaces with new ones
   * @param files - Array of File objects to add
   */
  const addImages = useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        return;
      }

      setIsUploading(true);

      const errors: string[] = [];
      const validFilesToProcess: File[] = [];

      // In edit mode: auto-replace (remove old images first)
      // In create mode: check remaining slots
      const isEditMode = !!productId;
      let remainingSlots = maxImages;

      if (!isEditMode) {
        const currentCount = images.length;
        remainingSlots = maxImages - currentCount;

        if (remainingSlots <= 0) {
          toast.error(`Maximum ${maxImages} images allowed`);
          setIsUploading(false);
          return;
        }
      }

      // Filter and validate files
      files.forEach((file) => {
        if (validFilesToProcess.length >= remainingSlots) {
          if (!isEditMode) {
            errors.push(`Only ${remainingSlots} slot(s) remaining`);
          }
          return;
        }

        const validation = validateFile(file);
        if (!validation.valid) {
          errors.push(`${file.name}: ${validation.error}`);
          return;
        }

        validFilesToProcess.push(file);
      });

      // If no valid files, show error and return
      if (validFilesToProcess.length === 0) {
        if (errors.length > 0) {
          toast.error(errors[0]);
        }
        setIsUploading(false);
        return;
      }

      // Process valid files and generate previews
      let processedCount = 0;
      const newImages: Image[] = [];

      validFilesToProcess.forEach((file) => {
        const reader = new FileReader();

        reader.onload = (e) => {
          const preview = e.target?.result as string;
          newImages.push({
            file,
            preview,
            id: undefined, // Will be assigned after API upload
          });

          processedCount++;

          // Update state once all files are processed
          if (processedCount === validFilesToProcess.length) {
            // If in edit mode with productId, upload images immediately
            if (productId) {
              // In edit mode: REPLACE all existing images with new ones
              uploadImagesToServer(validFilesToProcess, newImages, true);
            } else {
              // Create mode: just add to local state with blob previews
              setImages((prev) => [...prev, ...newImages]);
              setIsUploading(false);

              // Show first error if any
              if (errors.length > 0) {
                toast.error(errors[0]);
              }
            }
          }
        };

        reader.onerror = () => {
          processedCount++;
          errors.push(`${file.name}: Failed to read file`);

          // Update state once all files are processed
          if (processedCount === validFilesToProcess.length) {
            setImages((prev) => [...prev, ...newImages]);
            setIsUploading(false);

            if (errors.length > 0) {
              toast.error(errors[0]);
            }
          }
        };

        reader.readAsDataURL(file);
      });
    },
    [images.length, maxImages, validateFile, productId]
  );

  /**
   * Remove an image by index
   * @param index - Index of image to remove
   */
  const removeImage = useCallback((index: number) => {
    setImages((prev) => {
      if (index < 0 || index >= prev.length) {
        return prev;
      }

      const updated = [...prev];
      const removed = updated.splice(index, 1)[0];

      // Remove uploaded image ID if it exists
      if (removed.id) {
        setUploadedImageIds((prevIds) =>
          prevIds.filter((id) => id !== removed.id)
        );
      }

      return updated;
    });
  }, []);

  /**
   * Reorder images (for drag and drop functionality)
   * @param fromIndex - Original index of the image
   * @param toIndex - Target index to move the image to
   */
  const reorderImages = useCallback((fromIndex: number, toIndex: number) => {
    setImages((prev) => {
      if (
        fromIndex < 0 ||
        fromIndex >= prev.length ||
        toIndex < 0 ||
        toIndex >= prev.length ||
        fromIndex === toIndex
      ) {
        return prev;
      }

      const updated = [...prev];
      const [movedImage] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, movedImage);

      return updated;
    });
  }, []);

  /**
   * Clear all images
   */
  const clearAllImages = useCallback(() => {
    setImages([]);
    setUploadedImageIds([]);
  }, []);

  /**
   * Register uploaded image ID (called after successful API upload)
   * @param index - Index of the image in the array
   * @param imageId - ID returned from the API
   */
  const registerUploadedImage = useCallback((index: number, imageId: string) => {
    setImages((prev) => {
      if (index < 0 || index >= prev.length) {
        return prev;
      }

      const updated = [...prev];
      updated[index] = { ...updated[index], id: imageId };
      return updated;
    });

    setUploadedImageIds((prev) => [...prev, imageId]);
  }, []);

  /**
   * Get remaining slots available for images
   * @returns Number of remaining slots
   */
  const getRemainingSlots = useCallback(() => {
    return maxImages - images.length;
  }, [images.length, maxImages]);

  /**
   * Trigger file input click programmatically
   */
  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /**
   * Upload images to server immediately (for edit mode)
   * @param files - File objects to upload
   * @param imagePreviewData - Corresponding preview data
   * @param replaceExisting - If true, remove all existing images and only keep new ones (edit mode behavior)
   */
  const uploadImagesToServer = useCallback(
    async (files: File[], imagePreviewData: Image[], replaceExisting: boolean = false) => {
      try {
        if (!productId) {
          throw new Error('Product ID is required for image upload');
        }

        console.log('[useImageUpload] Starting upload:', {
          productId,
          fileCount: files.length,
          replaceExisting,
          previewDataCount: imagePreviewData.length,
        });

        // Call the API to upload images
        const response = await uploadProductImages(productId, files);

        console.log('[useImageUpload] Upload API response:', {
          responseCount: response.length,
          responses: response,
        });

        // Map uploaded response back to images
        // IMPORTANT: Backend returns ALL images (existing + newly uploaded).
        // New images are APPENDED at the end. So we must take the LAST N entries
        // from the response where N = number of files we uploaded.
        // Otherwise response[0] would be the OLD image URL, not the new one!
        const newImageResponses = response.length > imagePreviewData.length
          ? response.slice(-imagePreviewData.length)
          : response;

        console.log('[useImageUpload] Response mapping:', {
          totalResponseCount: response.length,
          newFilesCount: imagePreviewData.length,
          usingLastN: response.length > imagePreviewData.length,
          newImageResponseUrls: newImageResponses.map(r => r.url),
        });

        const uploadedImages = imagePreviewData.map((img, index) => {
          let imageUrl = newImageResponses[index]?.url || '';

          console.log(`[useImageUpload] Processing image ${index}:`, {
            originalUrl: newImageResponses[index]?.url,
            processedUrl: imageUrl,
            imageId: newImageResponses[index]?.imageId,
          });

          // Image URLs like /uploads/products/{id}/images/{filename} are served
          // via Vite proxy (/uploads → file-service) in dev, and via gateway in prod

          return {
            file: null,  // Already uploaded - prevent double upload on form submit
            id: newImageResponses[index]?.imageId || `img-${Date.now()}-${index}`,
            preview: img.preview,  // Keep base64 data URL for immediate display
            serverUrl: imageUrl,   // Store server URL separately for form submission
          };
        });

        console.log('[useImageUpload] Final uploaded images:', uploadedImages);

        // In edit mode with replaceExisting flag: remove old images and use only new ones
        if (replaceExisting) {
          console.log('[useImageUpload] Setting images (replace mode):', uploadedImages);
          setImages(uploadedImages);
          toast.success(`✓ Image(s) replaced successfully! Old images removed.`);
        } else {
          // Append mode (shouldn't happen but kept for safety)
          console.log('[useImageUpload] Appending images (append mode)');
          setImages((prev) => [...prev, ...uploadedImages]);
          toast.success(`${files.length} image(s) uploaded successfully!`);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to upload images';
        console.error('[useImageUpload] Upload error:', error);
        toast.error(`❌ Error: ${errorMessage}`);
        // Fallback: add images with blob previews even if upload fails
        if (replaceExisting) {
          console.log('[useImageUpload] Fallback: setting blob previews (replace mode)');
          setImages(imagePreviewData);
        } else {
          console.log('[useImageUpload] Fallback: appending blob previews');
          setImages((prev) => [...prev, ...imagePreviewData]);
        }
      } finally {
        setIsUploading(false);
      }
    },
    [productId]
  );

  /**
   * Set images directly (for restoring state or external updates)
   * @param newImages - Array of images to set
   */
  const setImagesDirectly = useCallback((newImages: Image[]) => {
    setImages(newImages);
    // Update uploaded image IDs from the new images
    const ids = newImages.filter((img) => img.id).map((img) => img.id as string);
    setUploadedImageIds(ids);
  }, []);

  // ============================================================================
  // Return Value
  // ============================================================================

  return {
    images,
    addImages,
    removeImage,
    reorderImages,
    isUploading,
    uploadedImageIds,
    clearAllImages,
    registerUploadedImage,
    getRemainingSlots,
    triggerFileInput,
    fileInputRef,
    setImages: setImagesDirectly,
  };
}

// ============================================================================
// Exports
// ============================================================================

export type { UseImageUploadReturn as UseImageUploadReturnType };
